// Mock API service for development without backend

const mockData = {
  clients: ['Netflix', 'Disney', 'Warner Bros', 'Sony Pictures'],
  jobs: {
    'Netflix': ['24-001_TheHeist', '24-002_ActionSequel', '24-003_Drama'],
    'Disney': ['24-010_Animation', '24-011_Fantasy'],
    'Warner Bros': ['24-020_Superhero', '24-021_Thriller'],
    'Sony Pictures': ['24-030_Comedy', '24-031_Horror']
  },
  sequences: {
    '24-001_TheHeist': ['VFX_010', 'VFX_020', 'VFX_030', 'EDIT_010'],
    '24-002_ActionSequel': ['ACT_010', 'ACT_020', 'EXP_010'],
    '24-003_Drama': ['DRM_010', 'DRM_020'],
    '24-010_Animation': ['ANM_010', 'ANM_020', 'ANM_030'],
    '24-011_Fantasy': ['FNT_010', 'FNT_020'],
    '24-020_Superhero': ['SH_010', 'SH_020', 'SH_030'],
    '24-021_Thriller': ['THR_010', 'THR_020'],
    '24-030_Comedy': ['COM_010', 'COM_020'],
    '24-031_Horror': ['HOR_010', 'HOR_020']
  },
  shots: {
    'VFX_010': ['sh010', 'sh020', 'sh030', 'sh040', 'sh050'],
    'VFX_020': ['sh010', 'sh020', 'sh030'],
    'VFX_030': ['sh010', 'sh020'],
    'EDIT_010': ['sh010', 'sh020', 'sh030', 'sh040'],
    'ACT_010': ['sh010', 'sh020', 'sh030'],
    'ACT_020': ['sh010', 'sh020'],
    'EXP_010': ['sh010', 'sh020', 'sh030', 'sh040'],
    'DRM_010': ['sh010', 'sh020'],
    'DRM_020': ['sh010', 'sh020', 'sh030'],
    'ANM_010': ['sh010', 'sh020', 'sh030'],
    'ANM_020': ['sh010', 'sh020'],
    'ANM_030': ['sh010', 'sh020', 'sh030', 'sh040'],
    'FNT_010': ['sh010', 'sh020'],
    'FNT_020': ['sh010', 'sh020', 'sh030'],
    'SH_010': ['sh010', 'sh020', 'sh030', 'sh040'],
    'SH_020': ['sh010', 'sh020'],
    'SH_030': ['sh010', 'sh020', 'sh030'],
    'THR_010': ['sh010', 'sh020'],
    'THR_020': ['sh010', 'sh020', 'sh030'],
    'COM_010': ['sh010', 'sh020'],
    'COM_020': ['sh010', 'sh020', 'sh030'],
    'HOR_010': ['sh010', 'sh020'],
    'HOR_020': ['sh010', 'sh020', 'sh030']
  },
  shotMetadata: {
    // Store metadata for shots created from ranges with increments
    // Key: sequenceName, Value: { shotDisplay: originalPattern, increment: number }
  }
};

class MockApiService {
  constructor() {
    this.delay = 500; // Simulate network delay
  }

  async simulateDelay() {
    return new Promise(resolve => setTimeout(resolve, this.delay));
  }

  // Authentication
  async verifyGoogleToken(token) {
    await this.simulateDelay();
    return {
      token: 'mock_jwt_token',
      user: {
        id: '123456789',
        name: 'Test User',
        email: 'test@example.com'
      }
    };
  }

  // Client operations
  async getClients() {
    await this.simulateDelay();
    return [...mockData.clients];
  }

  async createClient(name) {
    await this.simulateDelay();
    if (!mockData.clients.includes(name)) {
      mockData.clients.push(name);
      mockData.jobs[name] = [];
    }
    return { success: true, message: `Client "${name}" created successfully` };
  }

  // Job operations
  async getJobs(clientName) {
    await this.simulateDelay();
    return mockData.jobs[clientName] || [];
  }

  async createJob(clientName, jobData) {
    await this.simulateDelay();
    const jobName = jobData.name || jobData;
    if (!mockData.jobs[clientName]) {
      mockData.jobs[clientName] = [];
    }
    if (!mockData.jobs[clientName].includes(jobName)) {
      mockData.jobs[clientName].push(jobName);
      mockData.sequences[jobName] = [];
    }
    return { success: true, message: `Job "${jobName}" created successfully` };
  }

  // Sequence operations
  async getSequences(clientName, jobName) {
    await this.simulateDelay();
    return mockData.sequences[jobName] || [];
  }

  async createSequence(clientName, jobName, sequenceData) {
    await this.simulateDelay();
    const sequenceName = sequenceData.name || sequenceData;
    if (!mockData.sequences[jobName]) {
      mockData.sequences[jobName] = [];
    }
    if (!mockData.sequences[jobName].includes(sequenceName)) {
      mockData.sequences[jobName].push(sequenceName);
      mockData.shots[sequenceName] = [];
    }
    return { success: true, message: `Sequence "${sequenceName}" created successfully` };
  }

  // Shot operations
  async getShots(clientName, jobName, sequenceName) {
    await this.simulateDelay();
    return mockData.shots[sequenceName] || [];
  }

  async createShot(clientName, jobName, sequenceName, shotData, increment = null) {
    await this.simulateDelay();
    const shotName = shotData.name || shotData;
    
    if (!mockData.shots[sequenceName]) {
      mockData.shots[sequenceName] = [];
    }
    
    if (!mockData.shotMetadata[sequenceName]) {
      mockData.shotMetadata[sequenceName] = {};
    }
    
    // Check if it's a range pattern (e.g., "10-100")
    const rangeMatch = shotName.match(/^(\d+)-(\d+)$/);
    if (rangeMatch && increment) {
      const start = parseInt(rangeMatch[1]);
      const end = parseInt(rangeMatch[2]);
      const step = increment;
      
      // Generate individual shots based on range and increment
      const generatedShots = [];
      for (let i = start; i <= end; i += step) {
        const formattedShot = `sh${i.toString().padStart(3, '0')}`;
        generatedShots.push(formattedShot);
        if (!mockData.shots[sequenceName].includes(formattedShot)) {
          mockData.shots[sequenceName].push(formattedShot);
        }
      }
      
      // Store the metadata for display purposes
      mockData.shotMetadata[sequenceName][shotName] = {
        originalPattern: shotName,
        increment: increment,
        displayName: `${shotName}:${increment}`,
        generatedShots: generatedShots
      };
      
      // Also add the range display name to the shots list
      const displayName = `${shotName}:${increment}`;
      if (!mockData.shots[sequenceName].includes(displayName)) {
        mockData.shots[sequenceName].push(displayName);
      }
      
      return { success: true, message: `Shot range "${shotName}" with increment ${increment} created successfully` };
    } else {
      // Handle single shots, lists, or custom names
      if (!mockData.shots[sequenceName].includes(shotName)) {
        mockData.shots[sequenceName].push(shotName);
      }
      return { success: true, message: `Shot "${shotName}" created successfully` };
    }
  }

  // Application folder operations
  async addApplicationFolders(clientName, jobName, sequenceName, shotName, applications) {
    await this.simulateDelay();
    // Parameters are used for API compatibility - avoiding unused parameter warnings
    console.log(`Adding application folders for ${clientName}/${jobName}/${sequenceName}/${shotName}`);
    return { 
      success: true, 
      message: `Application folders added to "${shotName}": ${applications.join(', ')}` 
    };
  }

  // Combined structure creation
  async createStructure(structure) {
    await this.simulateDelay();
    
    console.log('Creating structure:', structure);
    
    // Simulate creating the structure
    const { client, job, sequence, shot, applications } = structure;
    
    // Add to mock data if new
    if (client && !mockData.clients.includes(client)) {
      await this.createClient(client);
    }
    
    if (job && client) {
      if (!mockData.jobs[client]?.includes(job)) {
        await this.createJob(client, job);
      }
    }
    
    if (sequence && job) {
      if (!mockData.sequences[job]?.includes(sequence)) {
        await this.createSequence(client, job, sequence);
      }
    }
    
    if (shot && sequence) {
      if (!mockData.shots[sequence]?.includes(shot)) {
        await this.createShot(client, job, sequence, shot);
      }
    }
    
    return { 
      success: true, 
      message: `Folder structure created successfully!
      Client: ${client}
      Job: ${job}
      Sequence: ${sequence}
      Shot: ${shot}
      Applications: ${applications?.join(', ') || 'None'}`
    };
  }

  // Utility methods
  async validateJobNumber(jobNumber) {
    await this.simulateDelay();
    const isValid = /^\d{2}-\d{3}$/.test(jobNumber);
    return { valid: isValid, message: isValid ? 'Valid format' : 'Format should be ##-###' };
  }

  async validateSequenceName(sequenceName) {
    await this.simulateDelay();
    const isValid = sequenceName.length <= 6 && /^[a-zA-Z0-9_-]+$/.test(sequenceName);
    const excludedFolders = ['concepts', 'editorial', 'global', 'io'];
    const isExcluded = excludedFolders.includes(sequenceName.toLowerCase());
    
    return { 
      valid: isValid && !isExcluded, 
      message: isValid && !isExcluded ? 'Valid' : 'Invalid sequence name' 
    };
  }

  async getJobStructure(clientName, jobName) {
    await this.simulateDelay();
    console.log(`Getting job structure for ${clientName}/${jobName}`);
    const sequences = mockData.sequences[jobName] || [];
    const structure = {};
    
    for (const seq of sequences) {
      structure[seq] = mockData.shots[seq] || [];
    }
    
    return structure;
  }

  // Template operations
  async getAvailableTemplates() {
    await this.simulateDelay();
    return {
      jobTemplates: ['job_template_v2'],
      shotTemplates: ['shot_template_v1']
    };
  }

  async getApplicationTemplates() {
    await this.simulateDelay();
    return ['maya', 'nuke', 'houdini', 'aftereffects', 'cinema4d', 'blender'];
  }
}

const mockApiService = new MockApiService();
export default mockApiService;